import { Controller } from "@hotwired/stimulus"
import { parseFixedAmountInput } from "../jsx/utils/decimalInput.js"

// EVA-295: the Rounding Policy band editor. Everything positional — derived
// lower limits, which row is the open-ended final band, input name indexes,
// whether delete is allowed — is recomputed from row order on every change,
// so add/remove never has to patch state incrementally. The server re-derives
// and validates the whole policy on submit; this controller is presentation.
export default class extends Controller {
  static targets = ["row", "body", "template"]
  static values = { setPreviousText: String }

  connect() {
    this.refresh()
  }

  add() {
    const fresh = this.templateTarget.content.querySelector("tr").cloneNode(true)
    fresh.querySelectorAll("input").forEach((input) => { input.value = "" })
    this.bodyTarget.appendChild(fresh)
    this.refresh()
  }

  remove(event) {
    if (this.rowTargets.length <= 1) return

    event.currentTarget.closest("tr").remove()
    this.refresh()
  }

  refresh() {
    const rows = this.rowTargets

    rows.forEach((row, index) => {
      const final = index === rows.length - 1

      row.querySelectorAll("[data-field]").forEach((input) => {
        input.name = `replace_all[rows][${index}][${input.dataset.field}]`
      })

      // The final band has no upper limit: hide and disable the input so the
      // field is not submitted, and show the "No upper limit" marker instead.
      const upperInput = row.querySelector('[data-field="upper_limit"]')
      row.querySelector("[data-rp-upper-wrap]").hidden = final
      upperInput.disabled = final
      row.querySelector("[data-rp-no-upper]").hidden = !final

      const lowerCell = row.querySelector("[data-rp-lower-cell]")
      if (index === 0) {
        lowerCell.textContent = this.format("0")
      } else {
        // parse() is the definition of "not a number"; a second `=== ""` test
        // here would put "  " or "abc" into the cell verbatim.
        const previousUpper = this.parse(rows[index - 1].querySelector('[data-field="upper_limit"]').value)
        lowerCell.textContent = previousUpper === null ? this.setPreviousTextValue : this.group(previousUpper)
      }

      row.querySelector("[data-rp-delete]").disabled = rows.length === 1
    })
  }

  // EVA-298: the amount fields are type="text" so they can carry grouping
  // separators, so a field is regrouped when the user leaves it. On blur
  // rather than per keystroke — formatting mid-entry means putting the caret
  // back, and there is nothing to gain from it here. An unparseable value is
  // left exactly as typed and goes to the server, which answers :not_a_number.
  reformat(event) {
    const input = event.currentTarget
    const value = this.parse(input.value)
    if (value === null) return

    input.value = this.group(value)
    this.refresh()
  }

  // "1,000,000" -> 1000000, null for anything that is not a number. Null rather
  // than 0 for the empty field on purpose: a blank Upper Limit is meaningful
  // (the row is being filled in, or is the open-ended final band).
  parse(raw) {
    return parseFixedAmountInput(raw)
  }

  format(raw) {
    const value = this.parse(raw)
    return value === null ? raw : this.group(value)
  }

  // NOT decimalInput.js's formatFixedAmountInput, which pins two decimals:
  // these amounts are whole currency units in practice and must render as
  // "1,000,000", matching RoundingPolicyHelper#format_rounding_amount's
  // "2dp only when present" rule on the read-only card.
  group(value) {
    return new Intl.NumberFormat("en-GB", { maximumFractionDigits: 2 }).format(value)
  }
}
